import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from '@/api/queryClient';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import gqlClient from '@/utils/gqlClient';

import {
  getCartIdCookie,
  invalidateCartData,
  saveCartIdCookie,
} from '../utils/cartUtils';

export const mutation = graphql(`
  mutation SavePromotion($cartId: String, $promotionCode: String!) {
    cartSavePromotion(cartId: $cartId, promotionCode: $promotionCode) {
      cartId
      resultCategory
      errorCode
      invalidReason
    }
  }
`);

export type SavePromotionResult = ResultOf<typeof mutation>;
export type SavePromotionVariables = VariablesOf<typeof mutation>;

function getErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object' || !('response' in err)) return undefined;
  const res = (err as { response: unknown }).response;
  if (!res || typeof res !== 'object' || !('status' in res)) return undefined;
  const status = (res as { status: number }).status;
  return typeof status === 'number' ? status : undefined;
}

const NOT_FOUND_RESULT = {
  cartSavePromotion: {
    cartId: null,
    resultCategory: 'user_exception' as const,
    errorCode: 'not_found' as const,
    invalidReason: 'not_found' as const,
  },
};

const UNEXPECTED_ERROR_RESULT = {
  cartSavePromotion: {
    cartId: null,
    resultCategory: 'unexpected_error' as const,
    errorCode: 'unexpected_error' as const,
    invalidReason: 'unexpected_error' as const,
  },
};

export function withQueryKey(vars: SavePromotionVariables) {
  return {
    queryKey: ['savePromotion', vars.cartId, vars.promotionCode],
    queryFn: async () => {
      if (!vars.promotionCode) return null;

      try {
        const data = await gqlClient().request(mutation, vars);
        const saveResult = data.cartSavePromotion;

        // Save new cart ID in case it changed.
        if (
          saveResult.cartId &&
          (!saveResult.invalidReason || !saveResult.errorCode)
        ) {
          saveCartIdCookie(saveResult.cartId);
        }

        // Cart was updated, so clear cache for any fetches that use the cart.
        invalidateCartData();

        return data;
      } catch (err: unknown) {
        const status = getErrorStatus(err);
        if (status === 404) return NOT_FOUND_RESULT;
        return UNEXPECTED_ERROR_RESULT;
      }
    },
  };
}

export async function performSavePromotion(promotionCode: string) {
  const queryClient = getQueryClient();
  const cartId = getCartIdCookie();
  return queryClient.fetchQuery(withQueryKey({ cartId, promotionCode }));
}

export function useSavePromotion(promotionCode: string) {
  const cartId = getCartIdCookie();
  return useQuery({
    ...withQueryKey({ cartId, promotionCode }),
    enabled: !!promotionCode && !!cartId,
  });
}
